/**
 * What a cloud removal says about itself while it runs.
 *
 * Split out of `sidebarProjectRemoval`, which is over 1,000 lines against a
 * 400-line cap. This decides copy and removes nothing, so it is the half a
 * reader can check without following a durable saga.
 */

import type { CmmdForgeRuntimeApiClient } from "~/environments/primary/runtimeConnectionClient";

import type { CmmdForgeCloudProjectRemovalRow } from "~/environments/primary/cloudProjectCatalog";

import { REMOVAL_STEP_LABELS, type RemovalStepLabel } from "./removalStepLabels";

export function removalPhaseLabel(
  phase: Awaited<ReturnType<CmmdForgeRuntimeApiClient["getCloudProjectRemoval"]>>["phase"],
): RemovalStepLabel {
  switch (phase) {
    case "delete_checkout_files":
      return REMOVAL_STEP_LABELS.deleteCheckoutFiles;
    case "destroy_runtime":
      return REMOVAL_STEP_LABELS.destroyRuntime;
    case "purge_execution_state":
      return REMOVAL_STEP_LABELS.purgeExecutionState;
    case "delete_planning_project":
      return REMOVAL_STEP_LABELS.deletePlanningProject;
    case "unregister":
    case "complete":
      return REMOVAL_STEP_LABELS.waitForRemoval;
  }
}

export type CloudRemovalPresentation =
  | { readonly kind: "none" }
  | {
      readonly kind: "active";
      readonly operationId: string;
      readonly stepLabel: RemovalStepLabel;
    }
  | {
      readonly kind: "retryable";
      readonly operationId: string;
      readonly stepLabel: RemovalStepLabel;
      readonly errorCode: string | null;
      readonly errorMessage: string;
      readonly deletePlanningProject: boolean;
      readonly projectId: number | null;
    };

/**
 * Converts CMMD's durable receipt into row policy. Terminal receipts do not
 * fence the row; pending/running and retryable receipts do. Retryable is kept
 * distinct so the row can replace its spinner with an explicit resume action.
 */
export function resolveCloudRemovalPresentation(
  removal: CmmdForgeCloudProjectRemovalRow | null,
  projectId: number | null,
): CloudRemovalPresentation {
  if (!removal) {
    return { kind: "none" };
  }
  const stepLabel = removalPhaseLabel(removal.phase);
  if (removal.status === "retryable") {
    return {
      kind: "retryable",
      operationId: removal.operationId,
      stepLabel,
      errorCode: removal.lastErrorCode,
      errorMessage: removal.lastErrorMessage ?? "CMMD could not finish project removal.",
      deletePlanningProject: removal.deletePlanningProject,
      projectId,
    };
  }
  return {
    kind: "active",
    operationId: removal.operationId,
    stepLabel,
  };
}
